import styled from '@emotion/styled';
import {
  CoreCurrencyInputComponent,
  CorePercentInputComponent,
} from '@dnhb/node_package';
import {
  currencyFormatter,
  percentageFormatter,
} from '@dnhb/formatters';
import Errors from './Errors';
import theme from "../../theme";
import {ChangeEventHandler, ComponentPropsWithoutRef, forwardRef} from "react";
import WithLabel from "./WithLabel";
import DisplayLine from "./DisplayLine";
import {useTranslation} from "react-i18next";

interface InputDivProps extends ComponentPropsWithoutRef<"div">{
  theme?: typeof theme;
  disabled?: boolean,
  width?: string|number,
  hasError?: boolean,
  height?: string|number,
}

const StyledInputDiv = styled.div<InputDivProps>(
  ({
    theme: { font, colors, space, fontSizes, fontWeights },
    disabled,
    width = '100%',
    hasError,
  }) => ({
    '> input': {
      width,
      fontFamily: font,
      //fontWeight: fontWeights.bold,
      fontSize: fontSizes?.m,
      color: colors?.primaryFont,
      padding: space?.s,
      border: `1px solid ${hasError ? colors?.alert : colors?.border}`,
      borderRadius: '4px',
      backgroundColor: disabled ? colors?.border : colors?.white,
      height: 40,

      '&:focus': {
        outline: 'none',
        borderColor: hasError ? colors?.alert : colors?.primary,
      },

      '&::placeholder': {
        color: colors?.secondaryFont,
        fontWeight: fontWeights?.thin,
      },
    },
  })
);

export interface InputProps extends InputDivProps {
  hasError?: boolean,
  showTextErrors?: boolean,
  errors?: string[]|boolean,
  isCurrency?: string,
  isPercentage?: boolean,
  label?: string,

  // from React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  name?: string;
  placeholder?: string;
  readOnly?: boolean;
  type?: string;
  value?: string | number;
  maxLength?: number,
  onChange?: ChangeEventHandler<HTMLInputElement>;

  // from: React.HTMLAttributes
  inputMode?: "text" | "none" | "tel" | "url" | "email" | "numeric" | "decimal" | "search",
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      showTextErrors,
      errors,
      name,
      value,
      onChange,
      isCurrency,
      inputMode,
      placeholder,
      autoFocus,
      type,
      isPercentage,
      maxLength,
      autoComplete,
      readOnly,
      disabled,
      label,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    if (readOnly) {
      let displayValue = value;
      if (value && isPercentage) displayValue = percentageFormatter(value);
      if (value && isCurrency) displayValue = currencyFormatter(value);
      return <DisplayLine label={label} value={displayValue} />;
    }

    let input = (
      <input
        ref={ref}
        autoComplete={autoComplete}
        value={value ?? ''}
        {...{
          name,
          onChange,
          inputMode,
          placeholder,
          type,
          autoFocus,
          maxLength,
          disabled,
        }}
      />
    );

    if (isPercentage) {
      input = (
        <CorePercentInputComponent
          onChangeHandler={onChange}
          placeholder="%"
          {...{ name, value, inputMode, autoFocus, type, disabled }}
        />
      );
    }

    if (isCurrency) {
      input = (
        <CoreCurrencyInputComponent
          onChangeHandler={onChange}
          placeholder={'€ ' + t("Bedrag")}
          {...{ name, value, inputMode, autoFocus, type, disabled }}
        />
      );
    }

    const content = (
      <StyledInputDiv hasError={!!errors} disabled={disabled} {...props}>
        {input}
        {showTextErrors ? <Errors {...{ errors }} /> : null}
      </StyledInputDiv>
    );

    return label ? <WithLabel label={label}>{content}</WithLabel> : content;
  }
);

Input.defaultProps = {
  showTextErrors: true,
};
export default Input;
